import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from 'react-icons/fi';
import { dateMask } from '~/utils/masks';
import * as St from './styles';

export const DATE_PICKER_FORMAT = 'DD/MM/YYYY';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const WEEK_DAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

export type DatePickerProps = {
  label?: string;
  name?: string;
  id?: string;
  /** Data no formato DD/MM/AAAA. */
  value?: string;
  /** Recebe a data no formato DD/MM/AAAA (ou parcial, enquanto digita). */
  onChange?: (value: string) => void;
  /** Datas fora do intervalo ficam desabilitadas no calendário (DD/MM/AAAA). */
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Campo vazio vira erro ("Informe a data"). */
  required?: boolean;
  /** Erro externo; tem prioridade sobre a validação interna. */
  errorMessage?: string;
  /** Avisa o erro da validação interna (undefined quando a data é válida ou vazia). */
  onValidate?: (error?: string) => void;
};

const parse = (value?: string): Moment | null => {
  if (!value || value.length !== 10) return null;
  const date = moment(value, DATE_PICKER_FORMAT, true);
  return date.isValid() ? date : null;
};

// Data incompleta só vira erro depois que o campo perde o foco, para não
// acusar erro enquanto a pessoa ainda está digitando.
export const validateDate = (
  value: string,
  minDate?: string,
  maxDate?: string,
  touched = true,
  required = false,
): string | undefined => {
  if (!value) return required && touched ? 'Informe a data' : undefined;
  if (value.length < 10) return touched ? 'Data incompleta' : undefined;
  const date = parse(value);
  if (!date) return 'Data inválida';
  const min = parse(minDate);
  const max = parse(maxDate);
  if (min && date.isBefore(min, 'day')) return `Data deve ser a partir de ${minDate}`;
  if (max && date.isAfter(max, 'day')) return `Data deve ser até ${maxDate}`;
  return undefined;
};

const buildDays =(month: Moment): Moment[] => {
  const start = month.clone().startOf('month').startOf('week');
  return Array.from({ length: 42 }, (_, i) => start.clone().add(i, 'days'));
};

const DatePicker = ({
  label,
  name,
  id,
  value = '',
  onChange = () => null,
  minDate,
  maxDate,
  placeholder = 'DD/MM/AAAA',
  disabled = false,
  required = false,
  errorMessage,
  onValidate,
}: DatePickerProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const validationError = validateDate(value, minDate, maxDate, touched, required);
  const error = errorMessage ?? validationError;

  useEffect(() => {
    onValidate?.(validateDate(value, minDate, maxDate, true, required));
  }, [value, minDate, maxDate, required]); // eslint-disable-line react-hooks/exhaustive-deps
  const selected = parse(value);
  const min = parse(minDate);
  const max = parse(maxDate);
  const [viewMonth, setViewMonth] = useState<Moment>(() =>
    (selected ?? moment()).clone().startOf('month'),
  );
  const containerRef = useRef<HTMLDivElement>(null);

  // Ao digitar uma data completa, o calendário acompanha o mês dela.
  useEffect(() => {
    if (selected) setViewMonth(selected.clone().startOf('month'));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  const days = useMemo(() => buildDays(viewMonth), [viewMonth]);

  const isOutOfRange = (day: Moment) =>
    (min && day.isBefore(min, 'day')) || (max && day.isAfter(max, 'day'));

  const toggle = () => {
    if (disabled) return;
    if (!open) setViewMonth((selected ?? moment()).clone().startOf('month'));
    setOpen((prev) => !prev);
  };

  const select = (day: Moment) => {
    if (isOutOfRange(day)) return;
    onChange(day.format(DATE_PICKER_FORMAT));
    setOpen(false);
  };

  const today = moment();

  return (
    <St.Container
      ref={containerRef}
      isFocused={focused || open}
      disabled={disabled}
      error={!!error}
    >
      {!!label && <label htmlFor={id ?? name}>{label}</label>}
      <St.Field>
        <input
          id={id ?? name}
          name={name}
          value={value}
          placeholder={placeholder}
          maxLength={10}
          disabled={disabled}
          autoComplete="off"
          aria-invalid={!!error}
          onFocus={() => {
            setFocused(true);
            setTouched(false);
          }}
          onBlur={() => {
            setFocused(false);
            setTouched(true);
          }}
          onChange={(e) => onChange(dateMask(e.target.value).masked)}
        />
        <St.IconButton
          type="button"
          aria-label="Abrir calendário"
          aria-expanded={open}
          disabled={disabled}
          onClick={toggle}
        >
          <FiCalendar />
        </St.IconButton>
      </St.Field>

      {open && (
        <St.Popover role="dialog" aria-label="Selecionar data">
          <St.CalendarHeader>
            <button
              type="button"
              aria-label="Ano anterior"
              onClick={() => setViewMonth((m) => m.clone().subtract(1, 'year'))}
            >
              <FiChevronsLeft />
            </button>
            <button
              type="button"
              aria-label="Mês anterior"
              onClick={() => setViewMonth((m) => m.clone().subtract(1, 'month'))}
            >
              <FiChevronLeft />
            </button>
            <strong>
              {MONTHS[viewMonth.month()]} {viewMonth.year()}
            </strong>
            <button
              type="button"
              aria-label="Próximo mês"
              onClick={() => setViewMonth((m) => m.clone().add(1, 'month'))}
            >
              <FiChevronRight />
            </button>
            <button
              type="button"
              aria-label="Próximo ano"
              onClick={() => setViewMonth((m) => m.clone().add(1, 'year'))}
            >
              <FiChevronsRight />
            </button>
          </St.CalendarHeader>

          <St.Grid>
            {WEEK_DAYS.map((day, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <St.WeekDay key={i}>{day}</St.WeekDay>
            ))}
            {days.map((day) => (
              <St.Day
                key={day.format('YYYY-MM-DD')}
                type="button"
                outside={day.month() !== viewMonth.month()}
                isToday={day.isSame(today, 'day')}
                selected={!!selected && day.isSame(selected, 'day')}
                disabled={!!isOutOfRange(day)}
                aria-label={day.format(DATE_PICKER_FORMAT)}
                onClick={() => select(day)}
              >
                {day.date()}
              </St.Day>
            ))}
          </St.Grid>

          <St.Footer>
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              Limpar
            </button>
            <button
              type="button"
              disabled={!!isOutOfRange(today)}
              onClick={() => select(today)}
            >
              Hoje
            </button>
          </St.Footer>
        </St.Popover>
      )}

      {error && <St.ErrorMessage role="alert">{error}</St.ErrorMessage>}
    </St.Container>
  );
};

export default DatePicker;
